<template>
  <!--
    IosInstallCoach — one-time Add-to-Home-Screen guide for iOS users (gap GR-3).

    Why this exists:
      On iOS, web push is categorically unavailable until the PWA is installed
      to the Home Screen (Safari 16.4+ only inside an installed/standalone PWA —
      the canRequestPushPermission() gate at usePushNotifications.js:89-95 returns
      false for un-installed iOS Safari). iOS has NO `beforeinstallprompt` API, so
      nothing prompts the user to install. Without this coach the entire push
      install beat is a silent dead-end for every iPhone/iPad viewer: they never
      learn they must tap Share → "Add to Home Screen" first. This sheet teaches
      that gesture visually so the push opt-in moment can actually be reached.

    Two modes (resolved from the UA each time it opens):
      - 'safari'  → real iOS Safari, not yet installed. Show the «…» → Share →
                    A2HS steps (the only browser on iOS that can install a PWA).
      - 'webview' → an in-app webview (Instagram/Facebook/etc.) or a non-Safari
                    iOS browser (Chrome/Firefox iOS) where Share → A2HS is NOT
                    available. Show "open this page in Safari" guidance instead of
                    failing silently (task focus area: in-app webview detection).

    Presentational: it never decides to open. The nudge scheduler
    (useEngagementNudges.js) decides and App.vue passes `opened` while it is
    the scheduler's current nudge; the sheet emits `closed`, and the scheduler
    snoozes it (PWA_SNOOZE_DAYS). On Safari it is the install nudge itself,
    after sign-in and a value moment (iOS 17.2+ copies the site's cookies into
    the installed app once, so a member who signed in first stays signed in);
    the webview variant is the one allowed before sign-in. A tap on the profile
    row opens it any time. Steps follow iOS 26 Safari: «…», Share, Add to Home
    Screen, Add, with «Open as Web App» left on.

    UX mirrors HuaweiBatteryCoach: F7 modal sheet, AR-first copy
    (notification.iosInstallCoach.*), RTL automatic via document dir, two CTAs
    that both close it (the scheduler's snooze). Mounted at App.vue root so it
    can appear over any route.
  -->
  <f7-sheet
    v-if="isVisible"
    backdrop
    :opened="open"
    :close-by-backdrop-click="false"
    :close-by-outside-click="false"
    :swipe-to-close="false"
    class="ios-install-coach-sheet sheet-scroll sheet-scroll-inner"
    @sheet:closed="onClosed"
  >
    <div class="ios-install-coach-content">
      <!-- Visual cue: Share → Add → Home Screen, the literal A2HS sequence. -->
      <div class="ios-install-coach-visual">
        <f7-icon
          f7="square_arrow_up"
          size="34px"
          color="purple"
        ></f7-icon>
        <f7-icon
          f7="arrow_right"
          size="18px"
          color="gray"
          class="ios-install-coach-visual-arrow"
        ></f7-icon>
        <f7-icon
          f7="plus_app"
          size="34px"
          color="purple"
        ></f7-icon>
        <f7-icon
          f7="arrow_right"
          size="18px"
          color="gray"
          class="ios-install-coach-visual-arrow"
        ></f7-icon>
        <f7-icon
          f7="house_fill"
          size="34px"
          color="purple"
        ></f7-icon>
      </div>

      <template v-if="mode === 'safari'">
        <h2 class="ios-install-coach-title">
          {{ $t('notification.iosInstallCoach.title') }}
        </h2>

        <p class="ios-install-coach-intro">
          {{ $t('notification.iosInstallCoach.intro') }}
        </p>

        <ol class="ios-install-coach-steps">
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step1') }}
          </li>
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step2') }}
          </li>
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step3') }}
          </li>
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step4') }}
          </li>
        </ol>

        <!-- Without the switch iOS makes a bookmark, which can never ask for
             notifications. Then the one line for older iOS (Share at the bottom). -->
        <p class="ios-install-coach-intro">
          {{ $t('notification.iosInstallCoach.webAppSwitch') }}
        </p>
        <p class="ios-install-coach-tip">
          {{ $t('notification.iosInstallCoach.olderIos') }}
        </p>

        <p class="ios-install-coach-tip">
          {{ $t('notification.iosInstallCoach.tip') }}
        </p>
      </template>

      <template v-else>
        <h2 class="ios-install-coach-title">
          {{ $t('notification.iosInstallCoach.webviewTitle') }}
        </h2>

        <p class="ios-install-coach-intro">
          {{ $t('notification.iosInstallCoach.webviewBody') }}
        </p>
      </template>

      <div class="ios-install-coach-actions">
        <f7-button
          large
          fill
          color="purple"
          class="ios-install-coach-action"
          @click="dismiss"
        >
          {{ $t('notification.iosInstallCoach.action') }}
        </f7-button>
        <f7-button
          class="ios-install-coach-link"
          @click="dismiss"
        >
          {{ $t('notification.iosInstallCoach.dismiss') }}
        </f7-button>
      </div>
    </div>
  </f7-sheet>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import { isIosSafari } from '@/platform/capabilities.js';

const props = defineProps({
  // Set by App.vue while the scheduler's current nudge is this coach.
  opened: { type: Boolean, default: false },
});

const emit = defineEmits(['closed']);

const isVisible = ref(false);
const open = ref(false);
// 'safari' (show A2HS steps) | 'webview' (show open-in-Safari guidance).
const mode = ref('safari');

// Both buttons close it; the scheduler records the snooze on `closed`.
function dismiss() {
  open.value = false;
}

function onClosed() {
  isVisible.value = false;
  emit('closed');
}

function show() {
  // Resolve the copy variant: real Safari gets the A2HS steps; everything else
  // on iOS (in-app webview, Chrome/Firefox iOS) gets the open-in-Safari nudge
  // so the install beat never fails silently.
  mode.value = isIosSafari() ? 'safari' : 'webview';
  isVisible.value = true;
  // Yield one frame for the v-if mount before the :opened watch fires the open
  // animation (otherwise the backdrop briefly stacks over the loading layout).
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      // The scheduler may have taken it back within the frame.
      if (props.opened) open.value = true;
    });
  } else {
    open.value = true;
  }
}

// Unmounted while open (the root re-rendering): report the close, or the
// scheduler would keep waiting for it.
onBeforeUnmount(() => {
  if (isVisible.value) emit('closed');
});

watch(
  () => props.opened,
  (wanted) => {
    if (wanted) show();
    else open.value = false;
  },
  { immediate: true },
);
</script>

<style scoped>
/* Sheet content — vertical stack with comfortable touch targets. Mirrors the
   HuaweiBatteryCoach sheet so the two coaches read as one design family. */
.ios-install-coach-sheet {
  --f7-sheet-bg-color: #ffffff;
  --f7-sheet-border-color: transparent;
  height: auto;
  max-height: 90%;
}

.ios-install-coach-content {
  padding:
    24px
    max(20px, env(safe-area-inset-right))
    max(20px, env(safe-area-inset-bottom))
    max(20px, env(safe-area-inset-left));
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: start;
}

.ios-install-coach-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
}

.ios-install-coach-visual-arrow {
  /* Mirror the directional arrow in RTL so the Share → Add → Home sequence
     reads correctly in Arabic. The RTL rule lives in a separate non-scoped
     <style> block below — Vue's :global() pseudo-class does NOT support a
     compound selector like :global(parent) .child inside <style scoped>; the
     compiler silently drops the child part and emits bare `html[dir='rtl'] {}`
     which transforms the entire page. */
  transform: scaleX(1);
}

.ios-install-coach-title {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
}

.ios-install-coach-intro {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #444444;
}

.ios-install-coach-steps {
  margin: 0;
  padding-inline-start: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ios-install-coach-step {
  font-size: 14.5px;
  line-height: 1.55;
  color: #1a1a1a;
}

.ios-install-coach-tip {
  margin: 4px 0 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #777777;
  text-align: center;
}

.ios-install-coach-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.ios-install-coach-action {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
}

.ios-install-coach-link {
  color: #777777;
  font-size: 14px;
}
</style>

<!-- Non-scoped: rules that must target html[dir='rtl'] on <html> cannot use
     Vue's :global() inside <style scoped> — the compiler drops the child
     selector and emits a bare html[dir='rtl'] {} that mirrors the whole page.
     Use a separate unscoped <style> block instead (same pattern as PoolShareHero.vue). -->
<style>
[dir='rtl'] .ios-install-coach-visual-arrow {
  transform: scaleX(-1);
}
</style>
